import { Add, Search } from '@mui/icons-material';
import { Box, Collapse, Divider, InputAdornment, Popover, TextField, Typography } from '@mui/material';
import type { LabelEntityType } from '@vy/types';
import type { Label } from '@vy/types';
import type { FC, MouseEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { VyDialog } from '../../feedback/VyDialog';
import { useDeleteLabel } from '../../../../hooks/requests/use-labels';
import { useDialog } from '../../../../hooks/use-dialog';
import { runMutation } from '../../../../utils/vy-mutation';
import { CreateLabelForm } from './CreateLabelForm';
import { EditLabelForm } from './EditLabelForm';
import { LabelItem } from './LabelItem';

type LabelsPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  labels: Label[];
  selectedLabelIds: string[];
  onToggle: (labelId: string) => void;
  entityType: LabelEntityType;
  onLabelCreated?: (labelId: string) => void;
  isUpdating?: boolean;
  openUpward?: boolean;
};

export const LabelsPopover: FC<LabelsPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  labels,
  selectedLabelIds,
  onToggle,
  entityType,
  onLabelCreated,
  isUpdating = false,
  openUpward = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [labelToDelete, setLabelToDelete] = useState<Label | null>(null);
  const editAnchorRef = useRef<HTMLElement | null>(null);

  const deleteLabelMutation = useDeleteLabel();
  const { isDialogOpen: isDeleteDialogOpen, handleOpen: openDeleteDialog, handleClose: closeDeleteDialog } = useDialog();

  const filteredLabels = useMemo(() => {
    if (!searchValue.trim()) {
      return labels;
    }
    const searchLower = searchValue.toLowerCase();
    return labels.filter((label) => label.name.toLowerCase().includes(searchLower));
  }, [labels, searchValue]);

  const handleToggleCreateMode = useCallback(() => {
    setIsCreateMode((prev) => !prev);
    setEditingLabel(null);
  }, []);

  const handleLabelCreated = useCallback(
    (labelId: string) => {
      setIsCreateMode(false);
      onLabelCreated?.(labelId);
    },
    [onLabelCreated],
  );

  const handleEditLabel = useCallback((label: Label, anchorEl: HTMLElement) => {
    setEditingLabel(label);
    setIsCreateMode(false);
    editAnchorRef.current = anchorEl;
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingLabel(null);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setEditingLabel(null);
  }, []);

  const handleRequestDeleteLabel = useCallback(
    (label: Label) => {
      setLabelToDelete(label);
      openDeleteDialog();
    },
    [openDeleteDialog],
  );

  const handleConfirmDeleteLabel = useCallback(() => {
    if (labelToDelete === null) {
      return;
    }
    void runMutation(
      deleteLabelMutation,
      { labelId: labelToDelete.id },
      {
        pending: 'Deleting label...',
        success: 'Label deleted',
        error: 'Failed to delete label',
      },
    );
    closeDeleteDialog();
    setLabelToDelete(null);
  }, [labelToDelete, deleteLabelMutation, closeDeleteDialog]);

  const handleClose = useCallback(() => {
    setSearchValue('');
    setIsCreateMode(false);
    setEditingLabel(null);
    onClose();
  }, [onClose]);

  const deleteDialogLabel = `Delete Label ${labelToDelete?.name ?? ''}`;

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: openUpward ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: openUpward ? 'bottom' : 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 240,
              maxHeight: '80vh',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Box sx={{ py: 1.5, px: 2 }}>
          <TextField
            data-vy="labels-popover-search"
            value={searchValue}
            onChange={(changeEvent) => setSearchValue(changeEvent.target.value)}
            placeholder="Search"
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                height: '40px',
                borderRadius: '8px',
                bgcolor: 'common.white',
                border: '1px solid',
                borderColor: 'divider',
                fontSize: '14px',
                fontWeight: '300',
              },
            }}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }
            }}
          />
        </Box>

        <Divider />

        {filteredLabels.length > 0 && (
          <>
            <Box sx={{ maxHeight: 144, overflowY: 'auto' }}>
              {filteredLabels.map((label) => {
                const handleToggle = () => onToggle(label.id);
                const handleEdit = (editEvent: MouseEvent<HTMLButtonElement>) => {
                  const anchorElement = editEvent?.currentTarget ?? document.body;
                  handleEditLabel(label, anchorElement);
                };
                const handleDelete = () => handleRequestDeleteLabel(label);

                return (
                  <LabelItem
                    key={label.id}
                    label={label}
                    selected={selectedLabelIds.includes(label.id)}
                    onToggle={handleToggle}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    disabled={isUpdating}
                  />
                );
              })}
            </Box>
            <Divider />
          </>
        )}

        <Collapse in={!isCreateMode}>
          <Box
            data-vy="labels-popover-create-new"
            onClick={handleToggleCreateMode}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Add sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Create new label
            </Typography>
          </Box>
        </Collapse>

        <Collapse in={isCreateMode}>
          <CreateLabelForm
            onCancel={() => setIsCreateMode(false)}
            onSuccess={handleLabelCreated}
          />
        </Collapse>
      </Popover>
      <Popover
        open={editingLabel !== null}
        anchorEl={editAnchorRef.current}
        onClose={handleCancelEdit}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 240,
              ml: 1,
            },
          },
        }}
      >
        {editingLabel && (
          <EditLabelForm label={editingLabel} onCancel={handleCancelEdit} onSuccess={handleEditSuccess} />
        )}
      </Popover>
      <VyDialog
        open={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        label={deleteDialogLabel}
        cancelButtonText="Cancel"
        ctaText="Yes, delete"
        sx={{
          '& .MuiDialogContent-root': {
            borderBottom: 'none',
          },
        }}
        ctaButtonProps={{
          sx: {
            backgroundColor: 'error.main',
            '&:hover': {
              backgroundColor: 'error.dark',
            },
          },
        }}
        onSubmit={handleConfirmDeleteLabel}
        dialogContentProps={{
          style: {
            maxWidth: '569px',
            minWidth: '569px',
          },
        }}
      >
        <Typography variant="body1">
          Are you sure you want to delete this label? This action is final and cannot be reversed.
        </Typography>
      </VyDialog>
    </>
  );
};
